import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '../../App';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  AlertTriangle, Search, RefreshCw, Bug, 
  Filter, ChevronLeft, ChevronRight, TrendingUp,
  Calendar, Clock, AlertCircle, AlertOctagon, Info,
  Server, Database, Wifi
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
});

// Severity badge colors
const getSeverityBadge = (severity) => {
  const colors = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    error: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };
  return colors[severity] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
};

// Severity icon
const SeverityIcon = ({ severity }) => {
  switch (severity) {
    case 'critical':
      return <AlertOctagon className="w-4 h-4 text-red-400" />;
    case 'error':
      return <AlertCircle className="w-4 h-4 text-orange-400" />;
    case 'warning':
      return <AlertTriangle className="w-4 h-4 text-amber-400" />;
    default:
      return <Info className="w-4 h-4 text-blue-400" />;
  }
};

// Error type badge colors
const getErrorTypeBadge = (errorType) => {
  const colors = {
    api_error: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    database_error: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    auth_error: 'bg-red-500/20 text-red-400 border-red-500/30',
    validation_error: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    system_error: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    connection_error: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  };
  return colors[errorType] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
};

// Format timestamp for display
const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-xl">
        <p className="text-zinc-400 text-xs mb-1">{label}</p>
        <p className="text-red-400 font-semibold">
          {payload[0].value} errors
        </p>
      </div>
    );
  }
  return null;
};

export default function SuperAdminIssuesLog() {
  const { user } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState(null);
  const [errorTypes, setErrorTypes] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedErrorType, setSelectedErrorType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [chartDays, setChartDays] = useState(30);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/platform/issues-stats?days=${chartDays}`,
        getAuthConfig()
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [chartDays]);

  const fetchIssues = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25'
      });
      
      if (search) params.append('search', search);
      if (selectedSeverity && selectedSeverity !== 'all') {
        params.append('severity', selectedSeverity);
      }
      if (selectedErrorType && selectedErrorType !== 'all') {
        params.append('error_type', selectedErrorType);
      }
      
      const response = await axios.get(
        `${API}/platform/issues-log?${params}`,
        getAuthConfig()
      );
      
      setIssues(response.data.issues);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch issues:', error);
      toast.error('Failed to load issues log');
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedSeverity, selectedErrorType]);

  const fetchErrorTypes = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/platform/issues-error-types`,
        getAuthConfig()
      );
      setErrorTypes(response.data.error_types);
    } catch (error) {
      console.error('Failed to fetch error types:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchErrorTypes();
  }, [fetchStats, fetchErrorTypes]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchIssues();
  };

  const handleRefresh = () => {
    fetchStats();
    fetchIssues();
    toast.success('Data refreshed');
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-3">
              <Bug className="w-7 h-7 text-red-500" />
              Issues Log
            </h1>
            <p className="text-zinc-500 mt-1">
              Monitor API errors and system issues in real-time
            </p>
          </div>
          <Button 
            onClick={handleRefresh}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="btn-refresh-issues"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Total Errors (30d)</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">
                    {stats?.summary?.total_errors || 0}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Critical</p>
                  <p className="text-2xl font-bold text-red-400 mt-1">
                    {stats?.summary?.critical_count || 0}
                  </p>
                </div>
                <AlertOctagon className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Today's Errors</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">
                    {stats?.daily_stats?.slice(-1)[0]?.error_count || 0}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wide">Affected Endpoints</p>
                  <p className="text-2xl font-bold text-zinc-100 mt-1">
                    {stats?.summary?.affected_endpoints || 0}
                  </p>
                </div>
                <Server className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error Trend Chart */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-zinc-100 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-red-500" />
                  Error Trend
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Daily error count over time
                </CardDescription>
              </div>
              <Select value={chartDays.toString()} onValueChange={(v) => setChartDays(parseInt(v))}>
                <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {stats?.daily_stats?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daily_stats}>
                    <defs>
                      <linearGradient id="colorErrors" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis 
                      stroke="#71717a"
                      tick={{ fill: '#71717a', fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="error_count"
                      stroke="#ef4444"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorErrors)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-500">
                  <div className="text-center">
                    <Bug className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No error data yet</p>
                    <p className="text-sm">Errors will appear here when they occur</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Failing Endpoints */}
        {stats?.top_endpoints?.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-zinc-100 text-sm flex items-center gap-2">
                <Server className="w-4 h-4 text-red-500" />
                Top Failing Endpoints
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {stats.top_endpoints.map((endpoint, i) => (
                  <Badge 
                    key={i}
                    className="bg-red-500/20 text-red-400 border-red-500/30 px-3 py-1 font-mono text-xs"
                  >
                    {endpoint.endpoint}: {endpoint.count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Issues Log Table */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-zinc-100 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Issues Log
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  {total.toLocaleString()} total issues
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder="Search by endpoint, error message..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-zinc-800 border-zinc-700 text-zinc-100"
                    data-testid="input-search-issues"
                  />
                </div>
                <Button type="submit" className="bg-red-500 hover:bg-red-600 text-white">
                  Search
                </Button>
              </form>
              
              <Select 
                value={selectedSeverity} 
                onValueChange={(v) => { setSelectedSeverity(v); setPage(1); }}
              >
                <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 text-zinc-300">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>

              <Select 
                value={selectedErrorType} 
                onValueChange={(v) => { setSelectedErrorType(v); setPage(1); }}
              >
                <SelectTrigger className="w-44 bg-zinc-800 border-zinc-700 text-zinc-300">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Error Type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Types</SelectItem>
                  {errorTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="rounded-lg border border-zinc-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400 w-24">Severity</TableHead>
                    <TableHead className="text-zinc-400">User</TableHead>
                    <TableHead className="text-zinc-400">Endpoint</TableHead>
                    <TableHead className="text-zinc-400">Error</TableHead>
                    <TableHead className="text-zinc-400">Type</TableHead>
                    <TableHead className="text-zinc-400 text-right">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                      </TableCell>
                    </TableRow>
                  ) : issues.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                        <Bug className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p>No issues found</p>
                        <p className="text-sm text-green-500">All systems running smoothly!</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    issues.map((issue) => (
                      <TableRow key={issue.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <SeverityIcon severity={issue.severity} />
                            <Badge className={`${getSeverityBadge(issue.severity)} text-xs capitalize`}>
                              {issue.severity}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {issue.user_id ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-zinc-100 text-sm truncate max-w-[180px]" title={issue.user_email}>
                                {issue.user_email || '-'}
                              </span>
                              <span className="text-zinc-500 text-xs font-mono truncate max-w-[180px]" title={issue.user_id}>
                                {issue.user_id.slice(0, 8)}...
                              </span>
                            </div>
                          ) : (
                            <span className="text-zinc-600 text-xs italic">Anonymous</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-500 text-xs font-medium px-1.5 py-0.5 bg-zinc-800 rounded">
                              {issue.method}
                            </span>
                            <span className="text-zinc-100 font-mono text-sm truncate max-w-xs">
                              {issue.endpoint}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm max-w-xs">
                          <div className="truncate" title={issue.error_message}>
                            {issue.error_message || '-'}
                          </div>
                          {issue.status_code && (
                            <span className="text-zinc-600 text-xs">
                              Status: {issue.status_code}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getErrorTypeBadge(issue.error_type)} text-xs`}>
                            {issue.error_type?.replace(/_/g, ' ') || 'unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 text-zinc-500 text-xs">
                            <Clock className="w-3 h-3" />
                            {formatTimestamp(issue.created_at)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-zinc-500 text-sm">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
